import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL, formatCurrency, formatDate } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
    CreditCard,
    TrendingUp,
    Users,
    DollarSign,
    BarChart3,
    RefreshCw,
    Search,
    Building2,
    Check,
    X,
    Clock,
    AlertTriangle,
    Loader2,
    ArrowUpDown,
} from "lucide-react";

const STATUS_LABELS = {
    active: { label: "Actif", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    trialing: { label: "Essai", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    past_due: { label: "En retard", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    cancelled: { label: "Annule", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
    expired: { label: "Expire", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" },
    trial_expired: { label: "Essai expire", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
};

export const AdminSubscriptionsPage = () => {
    const { token } = useAuth();
    const [subscriptions, setSubscriptions] = useState([]);
    const [revenue, setRevenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [updating, setUpdating] = useState(null);

    const fetchData = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const results = await Promise.allSettled([
            fetch(`${API_URL}/platform/subscriptions`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/platform/revenue`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
        ]);
        if (results[0].status === "fulfilled") setSubscriptions(results[0].value);
        if (results[1].status === "fulfilled") setRevenue(results[1].value);
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUpdateStatus = async (tenantId, newStatus) => {
        setUpdating(tenantId);
        try {
            const res = await fetch(`${API_URL}/platform/subscriptions/${tenantId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });
            if (!res.ok) throw new Error("Update failed");
            toast.success("Abonnement mis a jour");
            fetchData();
        } catch (e) {
            toast.error("Erreur lors de la mise a jour");
        } finally {
            setUpdating(null);
        }
    };

    const filteredSubs = subscriptions.filter((s) => {
        const matchSearch = !searchTerm || s.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === "all" || s.status === statusFilter;
        return matchSearch && matchStatus;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="admin-subscriptions-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Abonnements
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Gestion des abonnements et revenus
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-subs-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </div>

            {/* Revenue Stats */}
            {revenue && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="revenue-stats">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                    <DollarSign className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">MRR</p>
                                    <p className="text-lg font-bold">{formatCurrency(revenue.mrr)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">ARR</p>
                                    <p className="text-lg font-bold">{formatCurrency(revenue.arr)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                    <CreditCard className="w-5 h-5 text-violet-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Abonnements actifs</p>
                                    <p className="text-lg font-bold">{revenue.active_subscriptions}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <BarChart3 className="w-5 h-5 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Taux de churn</p>
                                    <p className="text-lg font-bold">{revenue.churn_rate}%</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Revenue Summary */}
            {revenue && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Revenus globaux</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                            <div>
                                <p className="text-xs text-muted-foreground">Revenu total</p>
                                <p className="text-2xl font-bold text-foreground">{formatCurrency(revenue.total_revenue)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Ce mois-ci</p>
                                <p className="text-2xl font-bold text-green-600">{formatCurrency(revenue.monthly_revenue)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Distribution des plans</p>
                                <div className="flex gap-2 mt-1 flex-wrap">
                                    {Object.entries(revenue.plans_distribution || {}).map(([plan, count]) => (
                                        <Badge key={plan} variant="outline" className="text-xs">
                                            {plan}: {count}
                                        </Badge>
                                    ))}
                                    {Object.keys(revenue.plans_distribution || {}).length === 0 && (
                                        <span className="text-xs text-muted-foreground">Aucun abonnement</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher par entreprise..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-subscriptions"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="status-filter">
                        <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="active">Actif</SelectItem>
                        <SelectItem value="trialing">Essai</SelectItem>
                        <SelectItem value="cancelled">Annule</SelectItem>
                        <SelectItem value="expired">Expire</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Subscriptions Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b bg-muted/50">
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Entreprise</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Plan</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Statut</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Cycle</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Utilisateurs</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Echeance</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubs.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center py-12 text-muted-foreground">
                                            Aucun abonnement trouve
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSubs.map((sub) => {
                                        const stCfg = STATUS_LABELS[sub.status] || STATUS_LABELS.expired;
                                        return (
                                            <tr key={sub.subscription_id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`sub-row-${sub.tenant_id}`}>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Building2 className="w-4 h-4 text-muted-foreground" />
                                                        <span className="text-sm font-medium">{sub.tenant_name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge variant="outline" className="text-xs capitalize">{sub.plan_id}</Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge className={`text-xs ${stCfg.color}`}>{stCfg.label}</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {sub.billing_cycle === "yearly" ? "Annuel" : "Mensuel"}
                                                </td>
                                                <td className="px-4 py-3 text-sm">{sub.user_count}</td>
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {formatDate(sub.current_period_end)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-1">
                                                        {sub.status === "active" || sub.status === "trialing" ? (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-red-600 hover:text-red-700 text-xs h-7"
                                                                onClick={() => handleUpdateStatus(sub.tenant_id, "cancelled")}
                                                                disabled={updating === sub.tenant_id}
                                                                data-testid={`disable-sub-${sub.tenant_id}`}
                                                            >
                                                                {updating === sub.tenant_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
                                                                Desactiver
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-green-600 hover:text-green-700 text-xs h-7"
                                                                onClick={() => handleUpdateStatus(sub.tenant_id, "active")}
                                                                disabled={updating === sub.tenant_id}
                                                                data-testid={`enable-sub-${sub.tenant_id}`}
                                                            >
                                                                {updating === sub.tenant_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                                                Activer
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
